import { supabase } from '@/lib/supabase';

/**
 * POST to one of our own /api routes with the caller's Supabase session
 * attached. The route verifies the token and the caller's role server-side.
 */
export async function apiPost<T = unknown>(path: string, body: Record<string, unknown>): Promise<T> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not signed in');

  const res = await fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await res.json().catch(() => ({} as Record<string, unknown>));
  if (!res.ok) throw new Error(errorMessage(json, res.status));

  return json as T;
}

/**
 * Pull a readable message out of an error body. Our own routes answer with
 * `{ error: 'message' }`, but /api/groq proxies the upstream response through
 * unchanged and Groq nests it as `{ error: { message } }` - without this the
 * toast would read "[object Object]".
 */
function errorMessage(json: unknown, status: number): string {
  const error = (json as { error?: unknown } | null)?.error;
  if (typeof error === 'string' && error) return error;

  const message = (error as { message?: unknown } | null)?.message;
  if (typeof message === 'string' && message) return message;

  return `Request failed (${status})`;
}
